/**
 * The draft, checked against the recording.
 *
 * After minutes are drafted from a transcript the server compares the two: is
 * every amount, rate and DIN in the draft something that was actually said? Is
 * an objection heard in the recording recorded in the minutes? Was an item
 * marked "not taken up" in fact discussed? The answers arrive as flags, and
 * this panel is where a company secretary reads them — before opening the
 * draft, not after signing it.
 *
 * A clean review is shown as clearly as a flagged one. "Nothing to flag" is a
 * finding, and the person approving should be able to see it was made.
 */

import { TbAlertTriangle, TbInfoCircle, TbCircleCheck, TbEar } from 'react-icons/tb';

const OUTCOME = {
  resolved: { label: 'Resolved', cls: 'bg-[#eef5f0] text-[#2c6045] border-[#b8d4c4]' },
  deferred: { label: 'Deferred', cls: 'bg-[#fdf6e9] text-[#7a5a2a] border-[#e3c9a0]' },
  noted: { label: 'Noted', cls: 'bg-[#f0f6f8] text-[#2c5560] border-[#b9cfd6]' },
  not_taken_up: { label: 'Not taken up', cls: 'bg-[#f3f1ec] text-[#6b6252] border-[#ddd6c6]' },
};

function Flag({ flag }) {
  const warn = flag.level === 'warn';
  return (
    <li className={`flex items-start gap-2 text-[12px] leading-relaxed ${warn ? 'text-[#8a3a3a]' : 'text-[#6b6252]'}`}>
      {warn
        ? <TbAlertTriangle size={14} className="mt-0.5 shrink-0 text-[#b04a4a]" />
        : <TbInfoCircle size={14} className="mt-0.5 shrink-0 text-[#8a8172]" />}
      <span>{flag.message}</span>
    </li>
  );
}

export default function DraftReviewPanel({ review }) {
  if (!review) return null;
  const { items = [], meeting = [], counts = { warn: 0, info: 0 } } = review;
  const clean = counts.warn === 0;

  return (
    <div className={`rounded-xl border-[1.5px] p-5 ${clean ? 'border-[#b8d4c4] bg-[#f7fbf8]' : 'border-[#e0b4b4] bg-[#fefaf9]'}`}>
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <TbEar size={17} className={clean ? 'text-[#2c6045]' : 'text-[#b04a4a]'} />
        <h3 className="text-[15px] font-bold text-[#2c2a24]">Checked against the recording</h3>
        {clean ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-[#b8d4c4] bg-white px-2 py-0.5 text-[11px] font-semibold text-[#2c6045]">
            <TbCircleCheck size={12} /> Nothing contradicts the recording
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full border border-[#e0b4b4] bg-white px-2 py-0.5 text-[11px] font-semibold text-[#8a3a3a]">
            <TbAlertTriangle size={12} /> {counts.warn} to verify before signing
          </span>
        )}
      </div>
      <p className="mb-4 text-[12px] text-[#6b6252]">
        Every amount, rate, identifier, name and outcome in the draft was traced back to what was said.
        {counts.info > 0 && ` ${counts.info} ${counts.info === 1 ? 'note' : 'notes'} to confirm.`}
      </p>

      {meeting.length > 0 && (
        <ul className="mb-4 space-y-1.5 rounded-lg border border-[#e8e2d5] bg-white px-3 py-2.5">
          {meeting.map((f, i) => <Flag key={i} flag={f} />)}
        </ul>
      )}

      <ol className="space-y-2">
        {items.map((item) => {
          const o = OUTCOME[item.outcome] || OUTCOME.resolved;
          const warns = item.flags.filter((f) => f.level === 'warn').length;
          return (
            <li
              key={item.number}
              className={`rounded-lg border bg-white px-3 py-2.5 ${warns ? 'border-[#e0b4b4]' : 'border-[#e8e2d5]'}`}
            >
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-[12px] font-bold tabular-nums text-[#8a8172]">{item.number}.</span>
                <span className="min-w-0 flex-1 text-[13px] font-semibold text-[#2c2a24]">{item.title}</span>
                <span className={`rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${o.cls}`}>{o.label}</span>
              </div>
              {item.flags.length > 0 && (
                <ul className="mt-1.5 space-y-1 pl-5">
                  {item.flags.map((f, i) => <Flag key={i} flag={f} />)}
                </ul>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
